/** @jsxImportSource preact */
import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';

interface FilterOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  isMobile: boolean;
  placeholder?: string;
  /** Optional hint displayed in the dropdown header, to the left of Select all / Clear all */
  infoText?: string;
}

export function MultiSelectDropdown({
  options,
  selected,
  onChange,
  isMobile,
  placeholder = 'All',
  infoText,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Compute panel position from trigger button's bounding rect
  const openPanel = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen(true);
  };

  const handleToggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const displayText = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) {
      const match = options.find((o) => o.value === selected[0]);
      return match?.label ?? selected[0];
    }
    return `${selected.length} selected`;
  };

  const hasSelection = selected.length > 0;
  const width = isMobile ? 140 : 120;

  // Compute the page's actual background for the floating panel
  const getPageBackground = (): string => {
    if (typeof window === 'undefined') return '#ffffff';
    const bg = getComputedStyle(document.body).backgroundColor;
    // If body bg is transparent/empty, fall back to white
    if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)') return '#ffffff';
    return bg;
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => isOpen ? setIsOpen(false) : openPanel()}
        style={{
          padding: '2px 6px',
          fontSize: 11,
          lineHeight: '14px',
          width,
          maxWidth: width,
          border: hasSelection
            ? '1px solid var(--gc-link, inherit)'
            : '1px solid var(--gc-control-border, currentColor)',
          borderRadius: 9,
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
          overflow: 'visible',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
          opacity: 0.8,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {displayText()}
        </span>
        <span style={{ fontSize: 8, flexShrink: 0, opacity: 0.6 }}>▾</span>
      </button>

      {isOpen && panelPos && (
        <div
          style={{
            position: 'fixed',
            top: panelPos.top,
            left: panelPos.left,
            minWidth: 180,
            maxHeight: 240,
            overflowY: 'auto',
            background: getPageBackground(),
            color: 'inherit',
            border: '1px solid var(--gc-control-border, currentColor)',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            zIndex: 1000,
            padding: '4px 0',
          }}
        >
          <div
            style={{
              padding: '4px 10px 6px',
              borderBottom: '1px solid var(--gc-control-border, #d1d5db)',
              marginBottom: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            {infoText && (
              <span style={{
                fontSize: 10,
                fontStyle: 'italic',
                color: 'var(--gc-text-muted, #6b7280)',
                whiteSpace: 'nowrap',
                marginRight: 'auto',
              }}>
                {infoText}
              </span>
            )}
            <button
              type="button"
              onClick={() => onChange(options.map((o) => o.value))}
              style={{
                border: 'none',
                background: 'none',
                color: 'var(--gc-link, inherit)',
                fontSize: 10,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              style={{
                border: 'none',
                background: 'none',
                color: 'var(--gc-link, inherit)',
                fontSize: 10,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Clear all
            </button>
          </div>
          {options.map((opt) => (
            <label
              key={opt.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                fontSize: 11,
                cursor: 'pointer',
                color: 'inherit',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'var(--gc-surface-muted, #f1f5f9)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => handleToggle(opt.value)}
                style={{ margin: 0, accentColor: 'var(--gc-link, inherit)' }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
